import * as React from "react"

import { Field, FieldDescription, FieldError, FieldLabel } from "@/components/ui/field"
import { cn } from "cn"

/** Shared label / description / error props for form field wrappers. */
export type FormFieldProps = {
  label: React.ReactNode
  required?: boolean
  description?: React.ReactNode
  error?: React.ReactNode
  fieldClassName?: string
}

/** Accessibility props injected into the control by {@link FormFieldShell}. */
export type FormFieldControlProps = {
  id: string
  "aria-describedby"?: string
}

/**
 * Required-aware field label (`*` is visual-only via `aria-hidden`).
 *
 * Prefer {@link FormInput}, {@link FormTextarea}, {@link FormSelect},
 * {@link FormCheckbox}, or {@link FormRadio} for typical fields; use this when
 * composing a custom control.
 */
export function FormFieldLabel({
  htmlFor,
  label,
  required,
}: {
  htmlFor?: string
  label: React.ReactNode
  required?: boolean
}) {
  return (
    <FieldLabel htmlFor={htmlFor}>
      {label}
      {required ? (
        <>
          {" "}
          <span aria-hidden="true" className="text-destructive">*</span>
        </>
      ) : null}
    </FieldLabel>
  )
}

/**
 * Field shell that wires label, description, and error accessibility:
 * `htmlFor` / `id`, `aria-describedby`, `data-invalid`, and `data-disabled`.
 *
 * Prefer the high-level wrappers for standard controls. Use the shell when the
 * control is custom (combobox, checkbox group, etc.).
 *
 * @example RHF
 * ```tsx
 * <Controller
 *   control={control}
 *   name="country"
 *   render={({ field }) => (
 *     <FormFieldShell
 *       label="Country"
 *       required
 *       htmlFor="country"
 *       error={errors.country?.message}
 *     >
 *       {(controlProps) => (
 *         <CountrySelect
 *           {...controlProps}
 *           value={field.value}
 *           onChange={field.onChange}
 *         />
 *       )}
 *     </FormFieldShell>
 *   )}
 * />
 * ```
 *
 * @example Controlled (no RHF)
 * ```tsx
 * <FormFieldShell label="Country" required htmlFor="country">
 *   {(controlProps) => (
 *     <CountrySelect
 *       {...controlProps}
 *       value={country}
 *       onChange={setCountry}
 *     />
 *   )}
 * </FormFieldShell>
 * ```
 */
export function FormFieldShell({
  label,
  required,
  description,
  error,
  fieldClassName,
  htmlFor,
  invalid,
  disabled,
  children,
}: FormFieldProps & {
  htmlFor: string
  invalid?: boolean
  disabled?: boolean
  children: (controlProps: FormFieldControlProps) => React.ReactNode
}) {
  const descriptionId = description ? `${htmlFor}-description` : undefined
  const errorId = error ? `${htmlFor}-error` : undefined
  const ariaDescribedBy =
    [descriptionId, errorId].filter(Boolean).join(" ") || undefined

  return (
    <Field
      className={cn('gap-1', fieldClassName)}
      data-invalid={invalid || undefined}
      data-disabled={disabled || undefined}
    >
      <FormFieldLabel htmlFor={htmlFor} label={label} required={required} />
      {children({ id: htmlFor, "aria-describedby": ariaDescribedBy })}
      {description ? (
        <FieldDescription id={descriptionId}>{description}</FieldDescription>
      ) : null}
      {error ? <FieldError id={errorId}>{error}</FieldError> : null}
    </Field>
  )
}
